import { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'utils/yup';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { AgentCompanyListingsQuery } from 'interfaceAdapter/queries/AgentCompanyListings';
import { BuyerRegistrationProfileInfoQuery } from 'interfaceAdapter/queries/BuyerRegistrationProfileInfo';
import { IPublicBuyer } from 'domain/entities/Buyer/Buyer';
import { useLoading } from 'application/contexts/useLoading';

const schema = yup
    .object({
        keyword: yup.string().nullable(),
        area: yup.array().of(yup.string()).nullable(),
        target_company_employee_size_lower: yup.string().nullable(),
        target_company_employee_size_upper: yup.string().nullable(),
        target_company_revenue_scale_lower: yup.string().nullable(),
        target_company_revenue_scale_upper: yup.string().nullable(),
        target_company_profit_level_lower: yup.string().nullable(),
        target_company_profit_level_upper: yup.string().nullable(),
        target_company_net_assets_level_lower: yup.string().nullable(),
        target_company_net_assets_level_upper: yup.string().nullable(),
        target_company_purchase_amount_lower: yup.string().nullable(),
        target_company_purchase_amount_upper: yup.string().nullable(),
        target_company_industries: yup.array().of(yup.string()).nullable(),
    })
    .required();

type SearchForm = yup.InferType<typeof schema>;

const PAGE_SIZE = 10;

export const useBuyerList = () => {
    const diContainer = useContext(diContainerContext);
    const agentCompanyListingsQuery = diContainer.resolve(AgentCompanyListingsQuery);
    const buyerRegistrationProfileInfoQuery = diContainer.resolve(BuyerRegistrationProfileInfoQuery);
    const { loading, setLoading } = useLoading();
    const [buyers, setBuyers] = useState<IPublicBuyer[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);

    // 検索条件
    const [areaChoices, setAreaChoices] = useState<string[][]>([]);
    const [employeeLowerChoices, setEmployeeLowerChoices] = useState<string[][]>([]);
    const [employeeUpperChoices, setEmployeeUpperChoices] = useState<string[][]>([]);
    const [revenueScaleLowerChoices, setRevenueScaleLowerChoices] = useState<string[][]>([]);
    const [revenueScaleUpperChoices, setRevenueScaleUpperChoices] = useState<string[][]>([]);
    const [profitLevelLowerChoices, setProfitLevelLowerChoices] = useState<string[][]>([]);
    const [profitLevelUpperChoices, setProfitLevelUpperChoices] = useState<string[][]>([]);
    const [netAssetsLevelLowerChoices, setNetAssetsLevelLowerChoices] = useState<string[][]>([]);
    const [netAssetsLevelUpperChoices, setNetAssetsLevelUpperChoices] = useState<string[][]>([]);
    const [purchaseAmountLowerChoices, setPurchaseAmountLowerChoices] = useState<string[][]>([]);
    const [purchaseAmountUpperChoices, setPurchaseAmountUpperChoices] = useState<string[][]>([]);
    const [industryChoices, setIndustryChoices] = useState<string[][]>([]);

    const { control, handleSubmit, getValues, watch, setValue } = useForm<SearchForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            keyword: '',
        },
    });

    useEffect(() => {
        setLoading(true);
        const fetchData = async () => {
            try {
                const offset = (currentPage - 1) * PAGE_SIZE;
                const resBuyers = await agentCompanyListingsQuery.fetchBuyers({
                    limit: PAGE_SIZE,
                    offset: offset,
                });
                setBuyers(resBuyers as IPublicBuyer[]);
                // 取得したデータがPAGE_SIZE件の場合は次のページがある可能性がある
                setHasNextPage(resBuyers.length === PAGE_SIZE);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [currentPage]);

    useEffect(() => {
        const fetchProfileInfo = async () => {
            const data = await buyerRegistrationProfileInfoQuery.fetch();
            const areaChoicesFormatted = data.area_choices
                .map((area) => area.children.map((child) => [child.code, child.name]))
                .flat();
            setAreaChoices(areaChoicesFormatted);
            setEmployeeLowerChoices(data.employee_lower_choices);
            setEmployeeUpperChoices(data.employee_upper_choices);
            setRevenueScaleLowerChoices(data.revenue_scale_lower_choices);
            setRevenueScaleUpperChoices(data.revenue_scale_upper_choices);
            setProfitLevelLowerChoices(data.profit_level_lower_choices);
            setProfitLevelUpperChoices(data.profit_level_upper_choices);
            setNetAssetsLevelLowerChoices(data.net_assets_level_lower_choices);
            setNetAssetsLevelUpperChoices(data.net_assets_level_upper_choices);
            setPurchaseAmountLowerChoices(data.purchase_amount_lower_choices);
            setPurchaseAmountUpperChoices(data.purchase_amount_upper_choices);
            setIndustryChoices(data.industry_choices);
        };
        fetchProfileInfo();
    }, []);

    // 検索実行
    const submit = handleSubmit(async (data) => {
        setLoading(true);
        setCurrentPage(1); // 検索時は1ページ目にリセット
        try {
            const offset = 0;
            const resBuyers = await agentCompanyListingsQuery.fetchBuyers({
                ...data,
                limit: PAGE_SIZE,
                offset: offset,
            });
            setBuyers(resBuyers as IPublicBuyer[]);
            setHasNextPage(resBuyers.length === PAGE_SIZE);
        } finally {
            setLoading(false);
        }
    });

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (hasNextPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePageChange = (page: number) => {
        if (page >= 1) {
            setCurrentPage(page);
        }
    };

    return {
        loading,
        buyers,
        areaChoices,
        employeeLowerChoices,
        employeeUpperChoices,
        revenueScaleLowerChoices,
        revenueScaleUpperChoices,
        profitLevelLowerChoices,
        profitLevelUpperChoices,
        netAssetsLevelLowerChoices,
        netAssetsLevelUpperChoices,
        purchaseAmountLowerChoices,
        purchaseAmountUpperChoices,
        industryChoices,
        getValues,
        setValue,
        watch,
        submit,
        control,
        agentCompanyListingsQuery,
        currentPage,
        hasNextPage,
        handlePreviousPage,
        handleNextPage,
        handlePageChange,
    };
};
